import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'

import { IneligibleAssignmentDTO } from 'models/creative'
import { MAX_CREATIVE_ELIGIBILITY_OVERRIDE_REASON_LENGTH } from 'constants/creative'
import { MUIInputField } from '../MUIInputField'
import React from 'react'
import { SystemMessage } from '../SystemMessage'
import { useTranslation } from 'react-i18next'

/**
 * @interface Props Input properties
 */
export interface Props {
  /** Assignments the creative is ineligible for, each with all the rules it failed */
  ineligibleAssignments: IneligibleAssignmentDTO[]
  /** Current override justification typed by the admin */
  overrideReason: string
  /** Whether the reason input is disabled (e.g. while the resubmit is in flight) */
  isDisabled?: boolean
  /** Called with the raw input value on every keystroke */
  onChangeOverrideReason: (overrideReason: string) => void
}

/**
 * @component
 * Failed-eligibility panel shown after the backend rejects an admin's manual creative selection
 * (ISSUE-2946). Lists every rule the creative failed, per assignment, and collects the mandatory
 * justification that lets the admin proceed anyway.
 *
 * Rule `code` and `category` are printed verbatim: the backend enums grow with every new rule and
 * always ship ahead of a frontend release, so the backend-authored `message` is the primary text and
 * only the surrounding chrome is translated.
 *
 * Presentational only — no data fetching, no gallery context. The parent owns the reason state and
 * the resubmit.
 *
 * @example
 * <CreativeEligibilityOverride
 *   ineligibleAssignments={ineligibleSelection.ineligibleAssignments}
 *   overrideReason={overrideReason}
 *   onChangeOverrideReason={setOverrideReason}
 * />
 */
export const CreativeEligibilityOverride: React.FC<Props> = ({
  ineligibleAssignments,
  overrideReason,
  isDisabled = false,
  onChangeOverrideReason,
}) => {
  const { t } = useTranslation(['assign_creative'])

  // `ineligibleAssignments` is a list in the shared rejection payload, so more than one entry is
  // representable and the per-assignment label is what would make such a list readable. With a single
  // entry — all the single-assignment endpoint can produce — the id is pure noise to an admin who
  // already knows which assignment they are on.
  const showAssignmentLabels = ineligibleAssignments.length > 1

  return (
    <Stack gap={1.5} marginTop={2}>

      <Stack gap={.4}>

        <Typography variant="text-md" fontWeight="semiBold" color={GRAY_900}>
          {t('eligibility_title')}
        </Typography>

        <Typography variant="text-sm" color={GRAY_700}>
          {t('eligibility_description')}
        </Typography>

      </Stack>

      {ineligibleAssignments.map((ineligibleAssignment) => (
        <SystemMessage
          key={ineligibleAssignment.assignmentId}
          variant="error"
          // Rule messages are backend-authored and can contain a bare `<` (thresholds read naturally
          // as "limit < 8h"), which <Trans> would treat as an opening tag and truncate the rest of
          // the sentence. The admin must see the whole explanation before overriding on it.
          isRawContent
          title={showAssignmentLabels ? t('eligibility_assignment_label', { assignmentId: ineligibleAssignment.assignmentId }) : undefined}
          message={
            <Stack gap={.8}>
              {ineligibleAssignment.failedRules.map((failedRule, index) => (
                // Codes are not unique within an assignment — two rules can fail with the same reason
                <Stack key={`${failedRule.code}-${index}`} gap={.1}>

                  <Typography variant="text-sm" fontWeight="semiBold" color={GRAY_900}>
                    {failedRule.code} · {failedRule.category}
                  </Typography>

                  <Typography variant="text-sm" color={GRAY_900}>
                    {failedRule.message}
                  </Typography>

                </Stack>
              ))}
            </Stack>
          }
        />
      ))}

      <MUIInputField
        required
        isMultiline
        sensitive
        disabled={isDisabled}
        value={overrideReason}
        label={t('eligibility_override_reason_label')}
        placeholder={t('eligibility_override_reason_placeholder')}
        maxLength={MAX_CREATIVE_ELIGIBILITY_OVERRIDE_REASON_LENGTH}
        hintText={t('eligibility_override_reason_hint', {
          current: overrideReason.length,
          max: MAX_CREATIVE_ELIGIBILITY_OVERRIDE_REASON_LENGTH,
        })}
        onChange={(e) => onChangeOverrideReason(e.target.value)}
      />

    </Stack>
  )
}
